import { apiClient, authApiClient, safeApi, ApiResponse } from '../../utils/axiosWithAuth';
import {
  CustomerDTO,
  CreateCustomerDTO,
  BillDTO,
  CreateBillDTO,
  CustomerSearchDTO,
  BillSearchDTO,
  CustomerPaginationResponse,
  BillPaginationResponse,
  ProductSearchResult,
  DailySummaryDTO,
} from '../../types/billing';

// ===============================
// CUSTOMER API FUNCTIONS
// ===============================

export const createCustomerAPI = (dto: CreateCustomerDTO) =>
  safeApi<CustomerDTO>(() =>
    apiClient.post<ApiResponse<CustomerDTO>>('/customers', dto)
  );

export const fetchCustomersAPI = (params: CustomerSearchDTO = {}) =>
  safeApi<CustomerPaginationResponse>(() => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.phone) queryParams.append('phone', params.phone);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    return apiClient.get<ApiResponse<CustomerPaginationResponse>>(
      `/customers${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    );
  });

export const searchCustomersByPhoneAPI = (phone: string) =>
  safeApi<CustomerDTO[]>(() =>
    apiClient.get<ApiResponse<CustomerDTO[]>>(`/customers/search-by-phone?phone=${phone}`)
  );

export const fetchCustomerByIdAPI = (uuid: string) =>
  safeApi<CustomerDTO>(() =>
    apiClient.get<ApiResponse<CustomerDTO>>(`/customers/${uuid}`)
  );

export const updateCustomerAPI = (uuid: string, dto: Partial<CreateCustomerDTO>) =>
  safeApi<CustomerDTO>(() =>
    apiClient.put<ApiResponse<CustomerDTO>>(`/customers/${uuid}`, dto)
  );

export const deleteCustomerAPI = (uuid: string) =>
  safeApi<{ deleted: boolean }>(() =>
    apiClient.delete<ApiResponse<{ deleted: boolean }>>(`/customers/${uuid}`)
  );

// ===============================
// PRODUCT SEARCH API FUNCTIONS
// ===============================

export const searchProductByCodeAPI = (code: string) =>
  safeApi<any>(() =>
    apiClient.get<ApiResponse<any>>(`/billing/products/search-by-code?code=${code}`)
  );

// ===============================
// BILL API FUNCTIONS
// ===============================

export const createBillAPI = (dto: CreateBillDTO) =>
  safeApi<{ bill: BillDTO; items: any[] }>(() =>
    apiClient.post<ApiResponse<{ bill: BillDTO; items: any[] }>>('/billing/bills', dto)
  );

export const fetchBillsAPI = (params: BillSearchDTO = {}) =>
  safeApi<BillPaginationResponse>(() => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.paymentType) queryParams.append('paymentType', params.paymentType);
    if (params.customerId) queryParams.append('customerId', params.customerId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.storeId) queryParams.append('storeId', params.storeId);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    return apiClient.get<ApiResponse<BillPaginationResponse>>(
      `/billing/bills${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    );
  });

export const fetchBillByIdAPI = (uuid: string, useAuth: boolean = true) =>
  safeApi<{ bill: BillDTO; items: any[] }>(() => {
    const client = useAuth ? apiClient : authApiClient;
    return client.get<ApiResponse<{ bill: BillDTO; items: any[] }>>(`/billing/bills/${uuid}`);
  });

// ===============================
// EXCHANGE / RETURN API
// ===============================

export interface ExchangeReturnItemDTO {
  billItemUuid: string;
  quantity: number;
}

export interface ExchangeNewItemDTO {
  productId: string;
  productCode: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  tax: number;
}

export interface ExchangeSaleDTO {
  reason: string;
  returnItems: ExchangeReturnItemDTO[];
  newItems?: ExchangeNewItemDTO[];
  settlementPaymentType?: string;
  customerId?: string;
}

export interface ExchangeSummaryDTO {
  returnValue: number;
  newItemsValue: number;
  netAmount: number;
  refundAmount: number;
  collectAmount: number;
  previousTotalAmount: number;
  newTotalAmount: number;
  settlementPaymentType?: string;
  settledToCredit?: boolean;
  refundToStoreCredit?: number;
  customerId?: string | null;
}

// Load a bill (with items) for the exchange flow — authenticated & store-scoped
// (uses the authed endpoint, NOT the public /billing/bills/:uuid)
export const fetchBillForExchangeAPI = (uuid: string) =>
  safeApi<{ bill: BillDTO; items: any[] }>(() =>
    apiClient.get<ApiResponse<{ bill: BillDTO; items: any[] }>>(`/billing/exchange/bill/${uuid}`)
  );

export const exchangeSaleAPI = (billUuid: string, dto: ExchangeSaleDTO) =>
  safeApi<{ bill: BillDTO; items: any[]; summary: ExchangeSummaryDTO }>(() =>
    apiClient.post<ApiResponse<{ bill: BillDTO; items: any[]; summary: ExchangeSummaryDTO }>>(
      `/billing/bills/${billUuid}/exchange`,
      dto
    )
  );

// ===============================
// E-INVOICE CUSTOMER API FUNCTIONS
// ===============================

export interface EInvoiceCustomerDTO {
  uuid: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  gstNumber?: string;
  panNumber?: string;
  billId: string;
  billNumber: string;
  billAmount: number;
  invoiceDate: Date;
  status: string;
  isPrinted: boolean;
  isEmailSent: boolean;
  createdBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateEInvoiceCustomerDTO {
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  country?: string;
  gstNumber?: string;
  panNumber?: string;
  billId: string;
  billNumber: string;
  billAmount: number;
  billDetails?: {
    items: any[];
    totals: {
      subtotal: number;
      totalDiscount: number;
      totalTax: number;
      totalAmount: number;
    };
    paymentDetails: {
      type: string;
      paidAmount: number;
      changeAmount?: number;
    };
  };
}

export const createEInvoiceCustomerAPI = (dto: CreateEInvoiceCustomerDTO) =>
  safeApi<EInvoiceCustomerDTO>(() =>
    apiClient.post<ApiResponse<EInvoiceCustomerDTO>>('/billing/e-invoice-customers', dto)
  );

export const fetchEInvoiceCustomersAPI = (params: {
  search?: string;
  page?: number;
  limit?: number;
} = {}) =>
  safeApi<{
    customers: EInvoiceCustomerDTO[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }>(() => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    return apiClient.get<ApiResponse<{
      customers: EInvoiceCustomerDTO[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>>(`/billing/e-invoice-customers${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
  });

export const updateEInvoiceCustomerStatusAPI = (uuid: string, data: {
  isPrinted?: boolean;
  isEmailSent?: boolean;
}) =>
  safeApi<EInvoiceCustomerDTO>(() =>
    apiClient.patch<ApiResponse<EInvoiceCustomerDTO>>(`/billing/e-invoice-customers/${uuid}/status`, data)
  );

// ===============================
// REPORTING API FUNCTIONS
// ===============================

export const fetchDailySummaryAPI = (date?: string) =>
  safeApi<DailySummaryDTO>(() => {
    const queryParams = date ? `?date=${date}` : '';
    return apiClient.get<ApiResponse<DailySummaryDTO>>(`/billing/reports/daily-summary${queryParams}`);
  });